/**
 * Filter Data Service
 *
 * Provides filter metadata (filter options, statistics, averages) for the explorer
 *
 * V2 (LAZY LOADING): Uses new /products/metadata endpoint for instant loading
 * V1 (LEGACY): Falls back to fetching all cells if backend endpoint unavailable
 *
 * Uses IndexedDB cache for fast loading
 */

import { deleteIndexedDBCache, getIndexedDBCache, setIndexedDBCache } from '../utils/indexedDBCache.js'
import { batchGetCells, getProductList, getProductMetadata } from './dataService.js'

const CACHE_KEY = 'filter-metadata-v17' // INCREMENTED: v17 backend now returns year range
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 DAYS (backend is cached)
// Stale-while-revalidate: a cache entry older than this is still served at once, but
// /products/metadata is fetched in the background and the fresh copy replaces it.
// Without this a changed manufacturer list stayed invisible for up to CACHE_DURATION.
const REVALIDATE_AFTER = 5 * 60 * 1000 // 5 minutes

const updateListeners = new Set()
let revalidationPromise = null
// Last successful background revalidation in this tab ({ data, timestamp }). Guards
// against a caller whose IndexedDB read started before that revalidation wrote the cache.
let latestRevalidated = null

/**
 * Subscribe to metadata refreshed by a background revalidation.
 * The listener only fires when the fresh metadata differs from the cached copy it replaces.
 * @param {(metadata: FilterMetadata) => void} listener
 * @returns {() => void} unsubscribe
 */
export function onFilterMetadataUpdate(listener) {
  updateListeners.add(listener)
  return () => {
    updateListeners.delete(listener)
  }
}

/**
 * Get filter metadata from cache or API
 * Uses new /metadata endpoint for instant loading (no ~100 cell fetches!)
 * A cached copy is returned immediately; if it is older than REVALIDATE_AFTER a background
 * fetch refreshes the cache and notifies onFilterMetadataUpdate() subscribers.
 * @returns {Promise<FilterMetadata>}
 */
export async function getFilterMetadata() {
  console.log('[filterDataService] Loading filter metadata...')

  // Try cache first
  const cachedEntry = await getCachedMetadata()
  if (cachedEntry) {
    const entry = latestRevalidated && latestRevalidated.timestamp >= cachedEntry.timestamp
      ? latestRevalidated
      : cachedEntry
    const cached = entry.data
    console.log('[filterDataService] ✓ Using cached metadata')
    console.log('[filterDataService] DEBUG CACHE: batchVariation =', cached.batchVariation)
    console.log('[filterDataService] DEBUG CACHE: aging =', cached.aging)
    if (Date.now() - entry.timestamp > REVALIDATE_AFTER) {
      revalidateInBackground(cached)
    }
    return cached
  }

  // Try new backend /metadata endpoint first (FAST!)
  try {
    console.log('[filterDataService] 🚀 Using new /metadata endpoint (lazy loading)')
    const metadata = await fetchMetadataFromBackend()

    // Validate metadata has required fields
    if (!metadata.cathodeChemistries?.length && !metadata.anodeChemistries?.length) {
      console.warn('[filterDataService] ⚠️ Metadata missing chemistry data, falling back to legacy')
      throw new Error('Metadata missing chemistry data')
    }

    // Cache for future use
    await cacheMetadata(metadata)

    return metadata
  } catch (error) {
    console.warn('[filterDataService] ⚠️ /metadata endpoint failed, falling back to legacy method:', error)

    // Fallback: Generate metadata from all cells (slow, legacy)
    const metadata = await generateFilterMetadataLegacy()
    await cacheMetadata(metadata)

    return metadata
  }
}

/**
 * Refresh the cached metadata from /products/metadata without blocking the caller.
 * Concurrent calls share one request. A failed or chemistry-less response keeps the cached
 * copy as it is: the slow legacy generation is only the cold-cache fallback.
 * @param {FilterMetadata} current - The cached metadata the caller was just given
 * @returns {Promise<FilterMetadata|null>} The fresh metadata, or null when it was not replaced
 */
function revalidateInBackground(current) {
  if (revalidationPromise) return revalidationPromise

  revalidationPromise = (async () => {
    try {
      const fresh = await fetchMetadataFromBackend()
      if (!fresh.cathodeChemistries?.length && !fresh.anodeChemistries?.length) {
        console.warn('[filterDataService] ⚠️ Revalidated metadata missing chemistry data, keeping cached copy')
        return null
      }

      latestRevalidated = { data: fresh, timestamp: Date.now() }
      await cacheMetadata(fresh)

      if (sameMetadata(current, fresh)) return fresh

      console.log('[filterDataService] ✓ Cached metadata was stale, delivering revalidated copy')
      for (const listener of [...updateListeners]) {
        try {
          listener(fresh)
        } catch (error) {
          console.warn('[filterDataService] Metadata update listener failed:', error)
        }
      }
      return fresh
    } catch (error) {
      console.warn('[filterDataService] ⚠️ Background revalidation failed, keeping cached copy:', error)
      return null
    } finally {
      revalidationPromise = null
    }
  })()

  return revalidationPromise
}

/**
 * Compare two metadata objects, ignoring the generation timestamps that differ on every fetch
 */
function sameMetadata(a, b) {
  const strip = (m) => ({ ...m, generatedAt: undefined, cachedAt: undefined })
  try {
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b))
  } catch {
    return false
  }
}

/**
 * Fetch metadata from backend /products/metadata endpoint
 * This is the new FAST path - single API call!
 * @returns {Promise<FilterMetadata>}
 */
async function fetchMetadataFromBackend() {
  const startTime = performance.now()

  // Call the new backend endpoint
  const apiMetadata = await getProductMetadata()

  // DEBUG: Log raw API response
  console.log('[filterDataService] DEBUG: Raw API response:', JSON.stringify(apiMetadata).slice(0, 500))
  console.log('[filterDataService] DEBUG: apiMetadata.filters:', apiMetadata.filters)

  // Transform backend response to frontend FilterMetadata format
  const metadata = transformApiMetadata(apiMetadata)

  const duration = Math.round(performance.now() - startTime)
  console.log(`[filterDataService] ✓ Metadata loaded from backend in ${duration}ms`)
  console.log(`[filterDataService] - ${metadata.manufacturers?.length || 0} manufacturers`)
  console.log(`[filterDataService] - ${metadata.chemistries?.length || 0} chemistries`)
  console.log(`[filterDataService] - ${metadata.cathodeChemistries?.length || 0} cathode materials`)
  console.log(`[filterDataService] - ${metadata.anodeChemistries?.length || 0} anode materials`)
  console.log(`[filterDataService] - ${metadata.ongoingAnalysis?.length || 0} ongoing analysis`)
  console.log(`[filterDataService] - ${metadata.formFactors?.length || 0} form factors`)

  return metadata
}

/**
 * Transform backend API response to frontend FilterMetadata format
 * @param {object} apiMetadata - Response from /products/metadata
 * @returns {FilterMetadata}
 */
function transformApiMetadata(apiMetadata) {
  const filters = apiMetadata.filters || {}
  const ranges = apiMetadata.ranges || {}
  const averages = apiMetadata.averages || {}

  // DEBUG: Log raw filters and ranges from backend
  console.log('[filterDataService] DEBUG: Raw filters from backend:', Object.keys(filters))
  console.log('[filterDataService] DEBUG: filters.cathode_materials:', filters.cathode_materials)
  console.log('[filterDataService] DEBUG: filters.anode_materials:', filters.anode_materials)
  console.log('[filterDataService] DEBUG: filters.analysis_ongoing:', filters.analysis_ongoing)
  console.log('[filterDataService] DEBUG: Raw ranges from backend:', Object.keys(ranges))
  console.log('[filterDataService] DEBUG: ranges.batch_variation:', ranges.batch_variation)
  console.log('[filterDataService] DEBUG: ranges.aging:', ranges.aging)

  // Helper: Transform filter array from backend format {value, count} to frontend {name, count}
  // Backend returns 'value' key, frontend expects 'name' key
  const transformFilterArray = (arr) => {
    if (!Array.isArray(arr)) return []
    return arr.map(item => ({
      name: item.value || item.name || '', // Backend uses 'value', frontend expects 'name'
      count: item.count || 0
    })).filter(item => item.name) // Remove empty entries
  }

  // Transform filter arrays from backend { value, count } to frontend { name, count } format
  const manufacturers = transformFilterArray(filters.manufacturers)
  const formFactors = transformFilterArray(filters.form_factors || filters.formats)
  const technologies = transformFilterArray(filters.technologies)
  const countries = transformFilterArray(filters.countries)

  // Chemistry: Get main chemistries from backend (split into cathode/anode if available)
  const cathodeChemistries = transformFilterArray(filters.cathode_materials || filters.chemistries)
  const anodeChemistries = transformFilterArray(filters.anode_materials)

  // Combined chemistries for backwards compatibility
  const allChemistryMap = new Map()
  for (const chem of cathodeChemistries) {
    if (chem.name) allChemistryMap.set(chem.name, (allChemistryMap.get(chem.name) || 0) + chem.count)
  }
  for (const chem of anodeChemistries) {
    if (chem.name) allChemistryMap.set(chem.name, (allChemistryMap.get(chem.name) || 0) + chem.count)
  }
  const chemistries = Array.from(allChemistryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .filter(item => item.name)
    .sort((a, b) => b.count - a.count)

  // Transform ranges from API format to FilterMetadata format
  // Backend format: { absolute: {...}, gravimetric: {...}, volumetric: {...} }
  // Frontend format: { abs: {...}, grav: {...}, vol: {...} }
  const transformRange = (rangeData, defaultUnit = '') => {
    if (!rangeData) {
      return {
        abs: createEmptyStats(defaultUnit),
        grav: createEmptyStats(defaultUnit + '/kg'),
        vol: createEmptyStats(defaultUnit + '/L')
      }
    }

    return {
      // Support both backend naming (absolute/gravimetric/volumetric) and frontend (abs/grav/vol)
      abs: transformRangeStats(rangeData.absolute || rangeData.abs, defaultUnit),
      grav: transformRangeStats(rangeData.gravimetric || rangeData.grav, defaultUnit + '/kg'),
      vol: transformRangeStats(rangeData.volumetric || rangeData.vol, defaultUnit + '/L')
    }
  }

  const transformRangeStats = (stats, unit) => {
    if (!stats) return createEmptyStats(unit)

    // Use nullish coalescing (??) instead of || to handle 0 correctly
    // 0 is a valid value for min/max, so we only want to use defaults for null/undefined
    const min = stats.min ?? 0
    const max = stats.max ?? 999999  // High default only if max is null/undefined
    const binCount = 10
    const binSize = max > min ? (max - min) / binCount : 1

    // We don't have per-bin counts from API, so create placeholder distribution
    // The UI can still use min/max/avg for filters
    const distribution = Array(binCount).fill(0).map((_, i) => ({
      rangeStart: Math.round((min + i * binSize) * 10) / 10,
      rangeEnd: Math.round((min + (i + 1) * binSize) * 10) / 10,
      count: Math.round((stats.count || 0) / binCount) // Approximate even distribution
    }))

    return {
      min: stats.min ?? 0,
      max: stats.max ?? 999999,  // High default only if max is null/undefined
      avg: stats.avg ?? 50,
      median: stats.median ?? 50,
      count: stats.count ?? 0,
      unit: stats.unit || unit,
      distribution
    }
  }

  const createEmptyStats = (unit) => ({
    min: 0,
    max: 999999,  // High default to not filter out cells when data is missing
    avg: 50,
    median: 50,
    count: 0,
    unit,
    distribution: []
  })

  // Transform simple range (no basis variants)
  const transformSimpleRange = (rangeData, unit) => {
    if (!rangeData) return createEmptyStats(unit)
    return transformRangeStats(rangeData, unit)
  }

  // Year is a special case - just min/max
  // If backend doesn't include year range, set to null so filterStore
  // can derive it from actual cell data instead of using a fake fallback
  const yearRangeRaw = ranges.year
  const yearStats = yearRangeRaw ? {
    min: yearRangeRaw.min,
    max: yearRangeRaw.max,
    avg: Math.round(((yearRangeRaw.min || 0) + (yearRangeRaw.max || 0)) / 2),
    median: Math.round(((yearRangeRaw.min || 0) + (yearRangeRaw.max || 0)) / 2),
    count: yearRangeRaw.count || 0,
    unit: 'year',
    distribution: []
  } : null

  // DEBUG: Log what we're getting for batch_variation and aging
  console.log('[filterDataService] DEBUG: batchVariation transformation:', {
    raw: ranges.batch_variation,
    hasData: !!ranges.batch_variation,
    min: ranges.batch_variation?.min,
    max: ranges.batch_variation?.max
  })
  console.log('[filterDataService] DEBUG: aging transformation:', {
    raw: ranges.aging,
    hasData: !!ranges.aging,
    min: ranges.aging?.min,
    max: ranges.aging?.max
  })

  return {
    version: 2, // v2 = from backend API
    generatedAt: Date.now(),
    totalCells: apiMetadata.total_products || 0,
    cachedAt: apiMetadata.cached_at,

    // Categorical filters (already in correct format)
    manufacturers,
    chemistries,
    cathodeChemistries,
    anodeChemistries,
    ongoingAnalysis: transformFilterArray(filters.analysis_ongoing),
    formFactors,
    countries,
    technologies,

    // Numerical ranges with statistics - ALL BASIS TYPES (abs, grav, vol)
    // Note: Backend returns SI units, frontend expects display units (Wh, Ah, etc.)
    // The backend /metadata endpoint already converts to display units!
    // Backend returns: ranges.energy, ranges.power, ranges.capacity, ranges.current
    energy: transformRange(ranges.energy, 'Wh'),
    power: transformRange(ranges.power, 'W'),
    capacity: transformRange(ranges.capacity, 'Ah'),
    current: transformRange(ranges.current, 'A'),
    voltage: transformSimpleRange(ranges.voltage, 'V'),
    mass: transformSimpleRange(ranges.mass, 'g'),
    volume: transformSimpleRange(ranges.volume, 'cm³'),
    // Batch Variation and Aging have specific reasonable defaults if backend doesn't provide them
    batchVariation: ranges.batch_variation
      ? transformSimpleRange(ranges.batch_variation, '%')
      : { min: 0, max: 10, avg: 2, median: 1.5, count: 0, unit: '%', distribution: [] },
    aging: ranges.aging
      ? transformSimpleRange(ranges.aging, '%')
      : { min: -5, max: 20, avg: 5, median: 3, count: 0, unit: '%', distribution: [] },
    year: yearStats,

    // Averages from API (for scatter plot centers, etc.)
    averages: averages
  }
}

/**
 * LEGACY: Generate filter metadata from all cells (slow fallback)
 * This is the old method that fetches ALL ~100 cells individually
 * Only used if /metadata endpoint is unavailable
 * @returns {Promise<FilterMetadata>}
 * @deprecated Use fetchMetadataFromBackend() instead
 */
async function generateFilterMetadataLegacy() {
  const startTime = performance.now()

  // Get all product IDs
  const productsData = await getProductList()
  const products = productsData.products || productsData || []
  console.log(`[filterDataService] Found ${products.length} products`)

  // Extract product IDs from product objects (support both product_id and id fields)
  const productIds = products.map(p => p.product_id || p.id || p).filter(id => typeof id === 'string')
  console.log(`[filterDataService] Extracted ${productIds.length} product IDs:`, productIds.slice(0, 5))

  // Batch load FULL normalized cell data (same as single cell page!)
  // Increased concurrency from 10 to 20 for faster metadata generation
  const batchStartTime = performance.now()
  const cellsDataMap = await batchGetCells(productIds, {}, 20) // concurrency: 20
  const batchEndTime = performance.now()
  console.log(`[filterDataService] ✓ Batch loaded ${productIds.length} cells in ${(batchEndTime - batchStartTime).toFixed(0)}ms`)
  console.log(`[filterDataService] Received cellsDataMap:`, cellsDataMap instanceof Map ? 'Map' : typeof cellsDataMap)

  // Convert Map to array
  const cellsData = Array.from(cellsDataMap.values())

  console.log(`[filterDataService] Processing ${cellsData.length} cells for metadata`)
  console.log(`[filterDataService] First cell sample:`, {
    id: cellsData[0]?.id,
    name: cellsData[0]?.name,
    manufacturer: cellsData[0]?.manufacturer,
    country: cellsData[0]?.country,
    technology: cellsData[0]?.technology,
    'chemical.main_chemistry': cellsData[0]?.chemical?.main_chemistry,
    'mechanical.format_group': cellsData[0]?.mechanical?.format_group,
    'electrical.E_c10_Wh': cellsData[0]?.electrical?.E_c10_Wh
  })

  // Aggregation sets and arrays
  const manufacturerMap = new Map() // manufacturer -> count
  const cathodeChemistryMap = new Map()  // cathode chemistry -> count (main_chemistry except LTO)
  const anodeChemistryMap = new Map()    // anode chemistry -> count (anode_material + LTO)
  const ongoingAnalysisMap = new Map()   // "analysis ongoing" -> count
  const formFactorMap = new Map()   // form factor -> count
  const countryMap = new Map()      // country -> count
  const technologyMap = new Map()   // technology -> count

    // Numerical value arrays for statistics (ALL THREE BASIS TYPES: abs, grav, vol)
    // ENERGY: W_C10 (J abs), W_C10_grav (J/kg), W_C10_vol (J/m³)
    const energyAbsValues = []      // W_C10 in J
    const energyGravValues = []     // W_C10_grav in J/kg → Wh/kg for UI
    const energyVolValues = []      // W_C10_vol in J/m³ → Wh/L for UI

    // POWER: P_peak (W abs), P_peak_grav (W/kg), P_peak_vol (W/m³)
    const powerAbsValues = []       // P_peak in W
    const powerGravValues = []      // P_peak_grav in W/kg
    const powerVolValues = []       // P_peak_vol in W/m³ → W/L for UI

    // CAPACITY: C_C10 (C abs), C_C10_grav (C/kg → Ah/kg), C_C10_vol (C/m³ → Ah/L)
    const capacityAbsValues = []    // C_C10 in C → Ah
    const capacityGravValues = []   // C_C10_grav in C/kg → Ah/kg
    const capacityVolValues = []    // C_C10_vol in C/m³ → Ah/L

    // CURRENT: I_peak (A abs), I_peak_grav (A/kg), I_peak_vol (A/m³)
    const currentAbsValues = []     // I_peak in A
    const currentGravValues = []    // I_peak_grav in A/kg
    const currentVolValues = []     // I_peak_vol in A/m³ → A/L

    const voltageValues = []
    const massValues = []
    const volumeValues = []
    const yearValues = []
    const batchVarValues = []       // CAP_spread_std_rel (relative variation %)
    const agingValues = []          // CAP_loss_rel (relative capacity loss %)  // Process each cell
  for (const cell of cellsData) {
    if (!cell) continue

    // Debug first 3 cells
    if (cellsData.indexOf(cell) < 3) {
      console.log(`[filterDataService] Cell ${cellsData.indexOf(cell)} data:`, {
        id: cell.id,
        name: cell.name,
        manufacturer: cell.manufacturer,
        country: cell.country,
        date: cell.date,
        year: cell.year,
        technology: cell.technology,
        'chemical.cathode_material_group': cell.chemical?.cathode_material_group,
        'chemical.anode_material_group': cell.chemical?.anode_material_group,
        'chemical.main_chemistry': cell.chemical?.main_chemistry,
        'mechanical.format_group': cell.mechanical?.format_group
      })
    }

    // Categorical values
    if (cell.manufacturer) {
      manufacturerMap.set(cell.manufacturer, (manufacturerMap.get(cell.manufacturer) || 0) + 1)
    } else {
      console.warn('[filterDataService] Cell without manufacturer:', cell.id, cell.name)
    }

    // Chemistry filter: Uses NEW API fields cathode_material_group and anode_material_group
    // - Cathode Chemistry: cathode_material_group (NMC, NCA, LFP, etc.)
    // - Anode Chemistry: anode_material_group (Graphite, Silicon, LTO, etc.)
    // - Analysis Ongoing: Cells where analysis is not yet complete
    const cathodeGroup = cell.chemical?.cathode_material_group
    const anodeGroup = cell.chemical?.anode_material_group

    // Fallback to old fields if new ones are empty (backward compatibility)
    const cathodeFallback = cell.chemical?.main_chemistry || cell.chemical?.cathode
    const anodeFallback = cell.chemical?.anode

    const effectiveCathode = cathodeGroup || cathodeFallback
    const effectiveAnode = anodeGroup || anodeFallback

    // Check for "analysis ongoing" state (case-insensitive)
    const isOngoing = (val) => val && val.toLowerCase().includes('analysis ongoing')

    if (isOngoing(effectiveCathode) || isOngoing(effectiveAnode)) {
      // Cell has analysis ongoing - add to ongoing category
      ongoingAnalysisMap.set('Analysis Ongoing', (ongoingAnalysisMap.get('Analysis Ongoing') || 0) + 1)
    } else {
      // Process cathode chemistry
      if (effectiveCathode) {
        cathodeChemistryMap.set(effectiveCathode, (cathodeChemistryMap.get(effectiveCathode) || 0) + 1)
      }

      // Process anode chemistry (avoid duplicates with cathode)
      if (effectiveAnode && effectiveAnode !== effectiveCathode) {
        anodeChemistryMap.set(effectiveAnode, (anodeChemistryMap.get(effectiveAnode) || 0) + 1)
      }
    }

    // Use mechanical.format_group (normalized data has mechanical nested)
    const formatGroup = cell.mechanical?.format_group || cell.mechanical?.format
    if (formatGroup) {
      formFactorMap.set(formatGroup, (formFactorMap.get(formatGroup) || 0) + 1)
    }

    if (cell.country) {
      countryMap.set(cell.country, (countryMap.get(cell.country) || 0) + 1)
    }

    // Technology: Use cell.technology if available, otherwise derive from chemical.cathode
    let technology = cell.technology
    if (!technology && cell.chemical?.cathode) {
      const chem = cell.chemical.cathode.toUpperCase()

      // Detailed categorization by chemistry type
      if (chem.includes('LTO') || chem.includes('TITANATE')) {
        technology = 'Lithium Titanate (LTO)'
      } else if (chem.includes('LFP') || chem.includes('LIFEPO4') || chem.includes('LiFePO4')) {
        technology = 'Lithium Iron Phosphate (LFP)'
      } else if (chem.includes('NMC')) {
        technology = 'Lithium-Ion (NMC)'
      } else if (chem.includes('NCA')) {
        technology = 'Lithium-Ion (NCA)'
      } else if (chem.includes('LCO') || chem.includes('LICOO2')) {
        technology = 'Lithium-Ion (LCO)'
      } else if (chem.includes('LMO') || chem.includes('LIMN2O4')) {
        technology = 'Lithium-Ion (LMO)'
      } else if (chem.includes('NA') || chem.includes('SODIUM')) {
        technology = 'Sodium-Ion'
      } else {
        technology = 'Lithium-Ion (Other)'
      }
    } else if (!technology) {
      technology = 'Lithium-Ion (Other)' // Default fallback
    }

    if (technology) {
      technologyMap.set(technology, (technologyMap.get(technology) || 0) + 1)
    }

    // Numerical values (only valid numbers) - ALL THREE BASIS TYPES
    // NOTE: electrical.* fields are already in UI units (Wh, Ah, W, etc.)

    // Energy - gravimetric (already in Wh/kg)
    if (typeof cell.electrical?.E_grav_Wh_per_kg === 'number' && !isNaN(cell.electrical.E_grav_Wh_per_kg)) {
      energyGravValues.push(cell.electrical.E_grav_Wh_per_kg)
    }
    // Energy - absolute (already in Wh)
    if (typeof cell.electrical?.E_c10_Wh === 'number' && !isNaN(cell.electrical.E_c10_Wh)) {
      energyAbsValues.push(cell.electrical.E_c10_Wh)
    }
    // Energy - volumetric (already in Wh/L)
    if (typeof cell.electrical?.E_vol_Wh_per_L === 'number' && !isNaN(cell.electrical.E_vol_Wh_per_L)) {
      energyVolValues.push(cell.electrical.E_vol_Wh_per_L)
    }

    // Power - gravimetric (already in W/kg)
    if (typeof cell.electrical?.P_grav_W_per_kg === 'number' && !isNaN(cell.electrical.P_grav_W_per_kg)) {
      powerGravValues.push(cell.electrical.P_grav_W_per_kg)
    }
    // Power - absolute (already in W)
    if (typeof cell.electrical?.P_peak_W === 'number' && !isNaN(cell.electrical.P_peak_W)) {
      powerAbsValues.push(cell.electrical.P_peak_W)
    }
    // Power - volumetric (already in W/L)
    if (typeof cell.electrical?.P_vol_W_per_L === 'number' && !isNaN(cell.electrical.P_vol_W_per_L)) {
      powerVolValues.push(cell.electrical.P_vol_W_per_L)
    }

    // Capacity - gravimetric (already in Ah/kg)
    if (typeof cell.electrical?.C_grav_Ah_per_kg === 'number' && !isNaN(cell.electrical.C_grav_Ah_per_kg)) {
      capacityGravValues.push(cell.electrical.C_grav_Ah_per_kg)
    }
    // Capacity - absolute (already in Ah)
    if (typeof cell.electrical?.C_c10_Ah === 'number' && !isNaN(cell.electrical.C_c10_Ah)) {
      capacityAbsValues.push(cell.electrical.C_c10_Ah)
    }
    // Capacity - volumetric (already in Ah/L)
    if (typeof cell.electrical?.C_vol_Ah_per_L === 'number' && !isNaN(cell.electrical.C_vol_Ah_per_L)) {
      capacityVolValues.push(cell.electrical.C_vol_Ah_per_L)
    }

    // Current - gravimetric (already in A/kg)
    if (typeof cell.electrical?.I_grav_A_per_kg === 'number' && !isNaN(cell.electrical.I_grav_A_per_kg)) {
      currentGravValues.push(cell.electrical.I_grav_A_per_kg)
    }
    // Current - absolute (already in A)
    if (typeof cell.electrical?.I_peak_A === 'number' && !isNaN(cell.electrical.I_peak_A)) {
      currentAbsValues.push(cell.electrical.I_peak_A)
    }
    // Current - volumetric (already in A/L)
    if (typeof cell.electrical?.I_vol_A_per_L === 'number' && !isNaN(cell.electrical.I_vol_A_per_L)) {
      currentVolValues.push(cell.electrical.I_vol_A_per_L)
    }

    // Voltage (already in V)
    if (typeof cell.electrical?.V_mean === 'number' && !isNaN(cell.electrical.V_mean)) {
      voltageValues.push(cell.electrical.V_mean)
    }

    // Mass (already in g from mechanical.m_cell_g)
    if (typeof cell.mechanical?.m_cell_g === 'number' && !isNaN(cell.mechanical.m_cell_g)) {
      massValues.push(cell.mechanical.m_cell_g)
    }

    // Volume (already in cm³ from mechanical.vol_cell_cm3)
    if (typeof cell.mechanical?.vol_cell_cm3 === 'number' && !isNaN(cell.mechanical.vol_cell_cm3)) {
      volumeValues.push(cell.mechanical.vol_cell_cm3)
    }

    // Batch variation (from batch.CAP_spread_std_rel, already in fraction → convert to %)
    if (typeof cell.batch?.CAP_spread_std_rel === 'number' && !isNaN(cell.batch.CAP_spread_std_rel)) {
      batchVarValues.push(cell.batch.CAP_spread_std_rel * 100) // fraction → percentage
    }
    // Aging (from aging.CAP_loss_rel, already in fraction → convert to %)
    if (typeof cell.aging?.CAP_loss_rel === 'number' && !isNaN(cell.aging.CAP_loss_rel)) {
      agingValues.push(Math.abs(cell.aging.CAP_loss_rel * 100)) // fraction → percentage
    }

    // Extract year (use year field directly if available, otherwise parse from date)
    if (typeof cell.year === 'number' && !isNaN(cell.year)) {
      if (cell.year >= 1990 && cell.year <= 2030) {
        yearValues.push(cell.year)
      }
    } else if (cell.date) {
      // Fallback: parse year from ISO date string
      const year = parseInt(cell.date.split('-')[0])
      if (!isNaN(year) && year >= 1990 && year <= 2030) {
        yearValues.push(year)
      }
    }
  }

  // Convert maps to sorted arrays with counts
  const manufacturers = Array.from(manufacturerMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count) // Sort by count descending

  // Chemistry split into cathode, anode, and ongoing
  const cathodeChemistries = Array.from(cathodeChemistryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  const anodeChemistries = Array.from(anodeChemistryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  const ongoingAnalysis = Array.from(ongoingAnalysisMap.entries())
    .map(([name, count]) => ({ name, count }))

  // Combined chemistries for backwards compatibility (all unique values)
  const allChemistryMap = new Map()
  for (const [name, count] of cathodeChemistryMap) {
    allChemistryMap.set(name, (allChemistryMap.get(name) || 0) + count)
  }
  for (const [name, count] of anodeChemistryMap) {
    allChemistryMap.set(name, (allChemistryMap.get(name) || 0) + count)
  }
  for (const [name, count] of ongoingAnalysisMap) {
    allChemistryMap.set(name, (allChemistryMap.get(name) || 0) + count)
  }
  const chemistries = Array.from(allChemistryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  const formFactors = Array.from(formFactorMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  const countries = Array.from(countryMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  const technologies = Array.from(technologyMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)

  console.log('[filterDataService] Aggregated manufacturers:', manufacturers)
  console.log('[filterDataService] Aggregated cathodeChemistries:', cathodeChemistries)
  console.log('[filterDataService] Aggregated anodeChemistries:', anodeChemistries)
  console.log('[filterDataService] Aggregated ongoingAnalysis:', ongoingAnalysis)
  console.log('[filterDataService] Aggregated formFactors:', formFactors)
  console.log('[filterDataService] ✅ Aggregation complete:', {
    manufacturers: manufacturers.length,
    cathodeChemistries: cathodeChemistries.length,
    anodeChemistries: anodeChemistries.length,
    ongoingAnalysis: ongoingAnalysis.length,
    formFactors: formFactors.length,
    technologies: technologies.length,
    manufacturerSample: manufacturers.slice(0, 3)
  })

  // Calculate statistics for numerical values - ALL THREE BASIS TYPES
  const metadata = {
    version: 1,
    generatedAt: Date.now(),
    totalCells: cellsData.length,

    // Categorical filters
    manufacturers,
    chemistries,           // Combined for backwards compatibility
    cathodeChemistries,    // NEW: Cathode/main chemistry (NMC, NCA, LFP, etc.)
    anodeChemistries,      // NEW: Anode chemistry (Graphite, Silicon, LTO, etc.)
    ongoingAnalysis,       // NEW: Cells with "analysis ongoing"
    formFactors,
    countries,
    technologies,

    // Numerical ranges with statistics - ALL BASIS TYPES (abs, grav, vol)
    energy: {
      abs: calculateStats(energyAbsValues, 'Wh'),
      grav: calculateStats(energyGravValues, 'Wh/kg'),
      vol: calculateStats(energyVolValues, 'Wh/L')
    },
    power: {
      abs: calculateStats(powerAbsValues, 'W'),
      grav: calculateStats(powerGravValues, 'W/kg'),
      vol: calculateStats(powerVolValues, 'W/L')
    },
    capacity: {
      abs: calculateStats(capacityAbsValues, 'Ah'),
      grav: calculateStats(capacityGravValues, 'Ah/kg'),
      vol: calculateStats(capacityVolValues, 'Ah/L')
    },
    current: {
      abs: calculateStats(currentAbsValues, 'A'),
      grav: calculateStats(currentGravValues, 'A/kg'),
      vol: calculateStats(currentVolValues, 'A/L')
    },
    voltage: calculateStats(voltageValues, 'V'),
    mass: calculateStats(massValues, 'g'),
    volume: calculateStats(volumeValues, 'cm³'),
    batchVariation: calculateStats(batchVarValues, '%'),
    aging: calculateStats(agingValues, '%'),
    year: calculateStats(yearValues, 'year'),
  }

  const duration = Math.round(performance.now() - startTime)
  console.log(`[filterDataService] ✓ Generated metadata in ${duration}ms`)
  console.log(`[filterDataService] - ${manufacturers.length} manufacturers`)
  console.log(`[filterDataService] - ${chemistries.length} chemistries`)
  console.log(`[filterDataService] - ${formFactors.length} form factors`)
  console.log(`[filterDataService] - Energy: ${metadata.energy.grav.min.toFixed(1)}-${metadata.energy.grav.max.toFixed(1)} Wh/kg`)
  console.log(`[filterDataService] - Power: ${metadata.power.grav.min.toFixed(0)}-${metadata.power.grav.max.toFixed(0)} W/kg`)
  console.log(`[filterDataService] - Batch Variation: ${metadata.batchVariation.min.toFixed(2)}-${metadata.batchVariation.max.toFixed(2)}%`)
  console.log(`[filterDataService] - Aging: ${metadata.aging.min.toFixed(2)}-${metadata.aging.max.toFixed(2)}%`)

  return metadata
}

/**
 * Calculate statistics for a numerical array
 * @param {number[]} values - Array of numbers
 * @param {string} unit - Unit string for display
 * @returns {object} Statistics object
 */
function calculateStats(values, unit) {
  if (!values || values.length === 0) {
    return {
      min: 0,
      max: 100,
      avg: 50,
      median: 50,
      count: 0,
      unit,
      distribution: []
    }
  }

  // Sort for percentiles
  const sorted = [...values].sort((a, b) => a - b)

  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  const sum = values.reduce((acc, v) => acc + v, 0)
  const avg = sum / values.length
  const median = sorted[Math.floor(sorted.length / 2)]

  // Check if this is year data (unit === 'year' and values are years)
  const isYearData = unit === 'year' && min >= 1900 && max <= 2100

  if (isYearData) {
    // For year data: create one bin per year
    const minYear = Math.floor(min)
    const maxYear = Math.ceil(max)
    const yearCount = maxYear - minYear + 1
    const yearDistribution = Array(yearCount).fill(0).map((_, i) => ({
      rangeStart: minYear + i,
      rangeEnd: minYear + i,
      count: 0
    }))

    // Count cells per year
    values.forEach(v => {
      const year = Math.round(v)
      const yearIndex = year - minYear
      if (yearIndex >= 0 && yearIndex < yearCount) {
        yearDistribution[yearIndex].count++
      }
    })

    return {
      min: minYear,
      max: maxYear,
      avg: Math.round(avg),
      median: Math.round(median),
      count: values.length,
      unit,
      distribution: yearDistribution
    }
  }

  // For non-year data: use 10 bins as before
  const binCount = 10
  const binSize = (max - min) / binCount
  const distribution = Array(binCount).fill(0)

  values.forEach(v => {
    const binIndex = Math.min(Math.floor((v - min) / binSize), binCount - 1)
    distribution[binIndex]++
  })

  return {
    min: Math.floor(min * 10) / 10, // Round to 1 decimal
    max: Math.ceil(max * 10) / 10,
    avg: Math.round(avg * 10) / 10,
    median: Math.round(median * 10) / 10,
    count: values.length,
    unit,
    distribution: distribution.map((count, i) => ({
      rangeStart: Math.round((min + i * binSize) * 10) / 10,
      rangeEnd: Math.round((min + (i + 1) * binSize) * 10) / 10,
      count
    }))
  }
}

/**
 * Get cached metadata from IndexedDB
 * @returns {Promise<{data: FilterMetadata, timestamp: number}|null>}
 */
async function getCachedMetadata() {
  try {
    const cached = await getIndexedDBCache(CACHE_KEY)

    if (!cached) {
      return null
    }

    // Check if cache is still valid
    const age = Date.now() - cached.timestamp
    if (age > CACHE_DURATION) {
      console.log(`[filterDataService] Cache expired (${Math.round(age / 1000 / 60)} minutes old)`)
      return null
    }

    if (!cached.data) return null
    return { data: cached.data, timestamp: cached.timestamp }
  } catch (error) {
    console.warn('[filterDataService] Cache read error:', error)
    return null
  }
}

/**
 * Cache metadata to IndexedDB
 * @param {FilterMetadata} metadata - Metadata to cache
 */
async function cacheMetadata(metadata) {
  try {
    await setIndexedDBCache(CACHE_KEY, {
      data: metadata,
      timestamp: Date.now()
    }, CACHE_DURATION)

    console.log('[filterDataService] ✓ Metadata cached successfully')
  } catch (error) {
    console.warn('[filterDataService] Cache write error:', error)
  }
}

/**
 * Invalidate filter metadata cache
 * Call this when cell data changes or after manual refresh
 */
export async function invalidateFilterCache() {
  latestRevalidated = null
  try {
    await deleteIndexedDBCache(CACHE_KEY)
    console.log('[filterDataService] ✓ Cache invalidated')
  } catch (error) {
    console.warn('[filterDataService] Cache invalidation error:', error)
  }
}

/**
 * TypeScript-style type definition for documentation
 * @typedef {object} FilterMetadata
 * @property {number} version - Metadata version
 * @property {number} generatedAt - Timestamp when generated
 * @property {number} totalCells - Total number of cells
 * @property {Array<{name: string, count: number}>} manufacturers - All manufacturers with counts
 * @property {Array<{name: string, count: number}>} chemistries - All chemistries with counts
 * @property {Array<{name: string, count: number}>} formFactors - All form factors with counts
 * @property {Array<{name: string, count: number}>} countries - All countries with counts
 * @property {Array<{name: string, count: number}>} technologies - All technologies with counts
 * @property {object} energy - Energy statistics
 * @property {object} power - Power statistics
 * @property {object} capacity - Capacity statistics
 * @property {object} voltage - Voltage statistics
 * @property {object} mass - Mass statistics
 * @property {object} volume - Volume statistics
 * @property {object} year - Year statistics
 */
